import styled, { css } from 'styled-components';
import Constants from '../../submodules/logictry_config/constants';
import {
  COLLECTION_GRID_MAX,
  COLLECTION_GRID_MIN,
} from '../LogicBaseCard';

/**
 * Shared layout + hero + section primitives for LogicBase-style pages.
 *
 * Originally defined inline in `containers/LogicBaseHomePage/index.jsx`. Extracted here
 * so that other surfaces (e.g. the logged-in Studio home) can render with the exact
 * same styled-components and stay visually locked to the LogicBase home page.
 *
 * Rendering must stay byte-identical for LogicBaseHomePage after the refactor —
 * do not tweak any declarations below without auditing the home page first.
 */

export const Root = styled.div`
  max-width: 100%;
  margin: 0;
  width: 100%;
  min-width: 0;
  padding: ${(p) => (p.$compact ? '1rem 0 0' : Constants.LogicBaseRootPaddingFull)};
  /** Bottom inset for floating Messages FAB (desktop) and mobile dock. */
  padding-bottom: ${(p) => (p.$compact ? '6rem' : '4.5rem')};
  box-sizing: border-box;
  overflow-x: hidden;
  background: transparent;

  ${Constants.MediaMobile} {
    position: relative;
  }
`;

export const ForYouFeed = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  padding-top: 0;
`;

export const Hero = styled.header`
  padding: ${(p) => (p.$withTabs ? '0.25rem 0 0' : '0.5rem 0 0')};
  border-bottom: none;
  margin-bottom: 0;
  min-width: 0;
  max-width: 100%;
`;

export const HeroTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
`;

export const HeroAccent = styled.div`
  width: 2.75rem;
  height: 4px;
  border-radius: 4px;
  background: linear-gradient(90deg, ${Constants.PrimaryColor}, rgba(30, 189, 83, 0.45));
  margin-bottom: 0.85rem;
`;

export const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.85rem, 4.5vw, 2.65rem);
  font-weight: 700;
  letter-spacing: -0.035em;
  color: ${Constants.DarkTextColor};
  line-height: 1.12;
`;

export const HeroLead = styled.p`
  margin: 0.65rem 0 0;
  font-size: 1.0625rem;
  color: ${Constants.MediumTextGray};
  line-height: 1.55;
  ${Constants.MediaMobile} {
    font-size: 0.9375rem;
    line-height: 1.5;
  }
`;

/** Discover rails: no vertical margin; spacing comes from ForYouFeed gap (same as Subscribed rows). */
export const DiscoverFeedSection = styled.section`
  margin: 0;
  min-width: 0;
  max-width: 100%;
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.2rem;
`;

export const SectionTitle = styled.h2`
  display: inline-flex;
  align-items: center;
  font-size: 1.45rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  color: ${Constants.DarkTextColor};
  margin: 0;
  ${Constants.MediaMobile} {
    font-size: 1.25rem;
  }
`;

/** Matches `CollectionRowTitle` in LogicBaseCollectionRow so labeled rails read like collection rows. */
export const RailRowTitle = styled.h2`
  margin: 0 0 0.7rem;
  font-size: ${Constants.LargeFontSize};
  ${Constants.MediaMobile} {
    font-size: ${Constants.SemiLargeFontSize};
  }
  font-weight: 500;
  line-height: 1.3;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SectionHint = styled.p`
  // margin: 0;
  font-size: 0.875rem;
  color: ${Constants.MediumTextGray};
  max-width: 36rem;
  line-height: 1.45;
`;

/** Right-gutter restore when HomeGutter bleeds rails (tabs, empty hints). */
export const HomeTabWrap = styled.div`
  min-width: 0;
  max-width: 100%;
`;

/** Tabs + active panel sit in one ForYouFeed child so hero spacing stays a single gap. */
export const HomeTabPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
  max-width: 100%;
`;

/** Profile-style collection shelves stacked under a home tab. */
export const CollectionRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  min-width: 0;
  max-width: 100%;
`;

export const HomeGutter = styled.div`
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  padding-left: ${(p) => (p.$compact ? Constants.LogicBasePageGutterCompact : Constants.LogicBasePageGutter)};
  padding-right: ${(p) => (p.$bleedRails ? 0 : (p.$compact ? Constants.LogicBasePageGutterCompact : Constants.LogicBasePageGutter))};

  ${(p) => p.$bleedRails && css`
    ${SectionHeader}, ${SectionHint}, ${HomeTabWrap} {
      padding-right: ${p.$compact ? Constants.LogicBasePageGutterCompact : Constants.LogicBasePageGutter};
    }
  `}
`;

export const CollectionGrid = styled.div`
  display: grid;
  /* auto-fit + 1fr: tiles grow and shrink with the pane. Item max-width
     stops a single column from using the full content stage. */
  grid-template-columns: repeat(auto-fit, minmax(${(p) => (p.$dense ? '14.5rem' : COLLECTION_GRID_MIN)}, 1fr));
  justify-items: start;
  gap: ${(p) => (p.$dense ? '0.7rem' : '0.9rem')};
  padding-top: 0.35rem;
  padding-right: ${(p) => (p.$flush ? 0 : Constants.LogicBasePageGutter)};
  box-sizing: border-box;
  > * {
    width: 100%;
    max-width: ${(p) => (p.$dense ? '14.5rem' : COLLECTION_GRID_MAX)};
  }
  ${Constants.MediaMobile} {
    grid-template-columns: repeat(auto-fit, minmax(min(100%, ${(p) => (p.$dense ? '14.5rem' : COLLECTION_GRID_MIN)}), 1fr));
    padding-right: ${(p) => (p.$flush ? 0 : Constants.LogicBasePageGutterCompact)};
  }
`;

/** Flex row inside horizontal Scrollable (parent element scrolls). */
export const RailTrack = styled.div`
  display: flex;
  gap: 0.9rem;
  min-width: 0;
  box-sizing: border-box;
  margin: 0;
  padding-top: 0.35rem;
  padding-right: ${Constants.LogicBasePageGutter};
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  ${Constants.MediaMobile} {
    padding-right: ${Constants.LogicBasePageGutterCompact};
  }

  & > * {
    scroll-snap-align: start;
    flex-shrink: 0;
  }
`;

export const OutlineBtn = styled.button`
  width: 100%;
  padding: 0.55rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${Constants.HairlineBorderRgba};
  background: ${Constants.isDarkMode ? '#161616' : 'white'};
  font-weight: 600;
  font-size: 0.8125rem;
  cursor: pointer;
  color: ${Constants.DarkTextColor};
  transition: border-color 0.15s ease, color 0.15s ease;
  &:hover {
    border-color: ${Constants.PrimaryColor};
    color: ${Constants.PrimaryColor};
  }
`;
